// Module Imports
import type {
  TUserRoleAssignedUsersQuery,
  TUserRolesContextQuery,
} from '@/modules/user-role/user-role.types'

export const userRoleEndpoints = {
  allPermissions: '/admin/user-roles/permissions', // GET
  list: '/admin/user-roles', // GET
  create: '/admin/user-roles', // POST
  detail: (id: string) => `/admin/user-roles/${id}`, // GET
  // One call backing the role-detail assign picker: paginated + searchable +
  // role-filterable players, plus the available roles. Replaces the
  // /admin/players fetch the page used to make.
  context: (query: TUserRolesContextQuery = {}) => {
    const searchParams = new URLSearchParams({
      page: String(query.page ?? 1),
      per_page: String(query.per_page ?? 20),
    })
    if (query.q?.trim()) searchParams.set('q', query.q.trim())
    if (query.role?.trim()) searchParams.set('role', query.role.trim())
    return `/admin/user-roles/context?${searchParams.toString()}`
  }, // GET
  assignedUsers: (id: string, query: TUserRoleAssignedUsersQuery) => {
    const searchParams = new URLSearchParams({
      page: String(query.page),
      per_page: String(query.per_page),
    })

    if (query.filter_field && query.filter_value?.trim()) {
      searchParams.set(
        'filters',
        `${query.filter_field}:${query.filter_value.trim()}`
      )
    }

    if (query.sort_field) {
      searchParams.set(
        'sort',
        `${query.sort_field}:${query.sort_order ?? 'asc'}`
      )
    }

    return `/admin/user-roles/${id}/assigned-users?${searchParams.toString()}`
  }, // GET
  update: (id: string) => `/admin/user-roles/${id}`, // PUT
  delete: (id: string) => `/admin/user-roles/${id}`, // DELETE
  assign: (userId: string) => `/admin/user-roles/assign/${userId}`, // PUT
  autoGrants: (id: string) => `/admin/user-roles/${id}/auto-grants`, // GET, PUT
}

export const userRoleBackendErrors = {
  ROLE_NAME_ALREADY_EXISTS: 'Bu oyuncu rol adı zaten kullanılıyor.',
  ROLE_IS_ASSIGNED:
    'Bu oyuncu rolü bir kullanıcıya atanmış olduğu için silinemez.',
  ROLE_NOT_FOUND: 'Oyuncu rolü bulunamadı.',
  ROLE_CREATE: 'Oyuncu rolü oluşturulurken hata oluştu.',
  ROLE_UPDATE: 'Oyuncu rolü güncellenirken hata oluştu.',
  ROLE_EDIT: 'Oyuncu rolü güncellenirken hata oluştu.',
  ROLE_DELETE: 'Oyuncu rolü silinirken hata oluştu.',
  PERMISSION_NOT_FOUND: 'Seçilen yetkilerden biri bulunamadı.',
  USER_NOT_FOUND: 'Kullanıcı bulunamadı.',
  USER_ROLE_ASSIGN: 'Oyuncu rol ataması güncellenirken hata oluştu.',
  GENERAL_ERROR: 'Beklenmeyen bir hata oluştu.',
} as const

export const userRoleSuccessMessages = {
  roleCreate: 'Oyuncu rolü başarıyla oluşturuldu.',
  roleEdit: 'Oyuncu rolü başarıyla güncellendi.',
  roleDelete: 'Oyuncu rolü başarıyla silindi.',
  roleAssign: 'Oyuncu rol ataması başarıyla güncellendi.',
}

// Seed roles ship with machine names (e.g. `player`). Map the known system
// roles to human-readable Turkish labels; unknown names pass through unchanged.
export const userRoleNameLabels: Record<string, string> = {
  player: 'Oyuncu',
  licensed_player: 'Lisanslı Oyuncu',
  league_participant: 'Lig Katılım Rolü',
}

export const formatUserRoleName = (name: string): string =>
  userRoleNameLabels[name] ?? name
